import { FeatureFlagName, ModulePermission, ProductCostingAccess, UserType } from '@luminovo/api-contracts';
import { isAuthorized, Permission } from '@luminovo/auth';
import { isPresent } from '@luminovo/commons';
import type { RouteStaticData } from '@luminovo/design-system';

import type { UserDetails } from '@/shared/data/contexts/CurrentUserDetailsContext';
import { ActionPermissionTest } from '@/shared/permissions/isAuthorizedForAction';
import { isAuthorizedForModule } from '@/shared/permissions/isAuthorizedForModule';
import type { RouteAccess } from '@/shared/routing/staticData';

/** What route predicates may read: `staticData` can't call hooks, so they get the session (`session.ts`). */
export type PagePermissionContext = {
    /** Legacy role permissions carried on the auth token. */
    permissions: Permission[];
    /** Advanced (SpiceDB) permissions, only consulted when `isAdvancedRoleManagementEnabled`. */
    modulePermissions: ModulePermission[];
    isAdvancedRoleManagementEnabled: boolean;
    /** Reports `true` while advanced role management is off, matching `useHasModulePermissions`. */
    hasPermissionForAction: (permissions: ActionPermissionTest, options?: { fallback: boolean }) => boolean;
    currentUser: UserDetails;
    /** `currentUser.userType`, lifted out because most predicates need nothing else. */
    userType: UserType;
    productCostingAccess: ProductCostingAccess;
    isFeatureFlagEnabled: (flag: FeatureFlagName) => boolean;
    /** A property of the host, here so predicates needn't read `window.location`. */
    isOrderPortalEnabled: boolean;
    /** Whether Purchasing is available on this deployment, a property of the host like `isOrderPortalEnabled`. */
    isPurchasingEnabled: boolean;
    /** Outside production some features are on regardless of their flag, e.g. the component importer. */
    isProductionEnvironment: boolean;
};

/** A permission check that looks at more than the permission list. Pure: no hooks, no module state. */
export type PagePermissionPredicate = (context: PagePermissionContext) => boolean;

export type PagePermissionTest = Permission[] | PagePermissionPredicate;

/** The advanced-role-management counterpart of `PagePermissionTest`. */
export type PageModulePermissionTest =
    | ModulePermission
    | { permissions: ModulePermission[]; type: 'any' | 'all' }
    | ((context: PagePermissionContext) => boolean);

/** The advanced permissions decide when the route opts in and the tenant has them on, else the legacy ones. */
export function isPageAuthorized(
    context: PagePermissionContext,
    requiredPermissions: PagePermissionTest,
    requiredModulePermissions?: PageModulePermissionTest,
): boolean {
    if (isPresent(requiredModulePermissions) && context.isAdvancedRoleManagementEnabled) {
        return typeof requiredModulePermissions === 'function'
            ? requiredModulePermissions(context)
            : isAuthorizedForModule(context.modulePermissions, requiredModulePermissions);
    }

    return typeof requiredPermissions === 'function'
        ? requiredPermissions(context)
        : isAuthorized(context.permissions, requiredPermissions);
}

/** Every match's `access` must pass, root first; a missing `access` or no matches fails, so no check goes missing. */
export function canOpenRoute(
    context: PagePermissionContext,
    matches: ReadonlyArray<Partial<RouteStaticData>>,
): boolean {
    return matches.length > 0 && matches.every(({ access }) => isAccessGranted(context, access));
}

function isAccessGranted(context: PagePermissionContext, access: RouteAccess | undefined): boolean {
    switch (access) {
        // Fails closed: a match of a route that declares nothing carries `{}`, so `access` can be undefined.
        case undefined:
            return false;
        case 'public':
        // Sign-in is enforced around this: `_authenticated`'s `beforeLoad` and `AuthenticatedLayout` redirect.
        case 'authenticated':
            return true;
        default:
            return isPageAuthorized(context, access.permissions, access.modulePermissions);
    }
}

/**
 * Thrown by `_authenticated`'s `beforeLoad` when `canOpenRoute` fails. The route's error component shows
 * `NotAuthorizedPage` in the signed-in shell, without the navbar, on the URL the user asked for. A refusal, not a
 * crash, so `reportRouteError` doesn't send it to Sentry.
 */
export class RouteAccessDeniedError extends Error {
    constructor() {
        super('The user may not open this route');
        this.name = 'RouteAccessDeniedError';
    }
}
